import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Alert, AlertDescription } from "../../components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Textarea } from "../../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import {
  Eye,
  EyeOff,
  Gift,
  Clock,
  Play,
  Pause,
  RefreshCw,
  CreditCard,
  Shield,
  AlertTriangle,
  CheckCircle,
  XCircle,
  History,
  User,
  Building,
  Search,
  Send,
  MessageSquare
} from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_API_URL;

export default function AdminBillingControls() {
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [overrideStatus, setOverrideStatus] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("customers");
  
  // Dialog states
  const [impersonateDialog, setImpersonateDialog] = useState(false);
  const [trialDialog, setTrialDialog] = useState(false);
  const [courtesyDialog, setCourtesyDialog] = useState(false);
  const [planDialog, setPlanDialog] = useState(false);
  const [suspendDialog, setSuspendDialog] = useState(false);
  const [notifyDialog, setNotifyDialog] = useState(false);
  
  // Form states
  const [trialDays, setTrialDays] = useState(14);
  const [trialReason, setTrialReason] = useState("");
  const [courtesyPlan, setCourtesyPlan] = useState("professional");
  const [courtesyDays, setCourtesyDays] = useState(30);
  const [courtesyReason, setCourtesyReason] = useState("");
  const [assignPlan, setAssignPlan] = useState("starter");
  const [assignReason, setAssignReason] = useState("");
  const [suspendReason, setSuspendReason] = useState("");
  const [notificationType, setNotificationType] = useState("payment_reminder");
  
  // Admin info (would come from auth context in real app)
  const adminId = "admin-gsk";
  const adminName = "GSK Admin";
  const adminRole = "super_admin";

  const fetchCustomers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/admin/settings/customers?limit=100`);
      setCustomers(res.data.customers || []);
    } catch (error) {
      console.error("Failed to fetch customers:", error);
    }
    setLoading(false);
  }, []);

  const fetchOverrideStatus = async (businessId) => {
    try {
      const res = await axios.get(`${API}/api/admin/super/business/${businessId}/override-status`);
      setOverrideStatus(res.data);
    } catch (error) {
      console.error("Failed to fetch override status:", error);
    }
  };

  const fetchAuditLogs = async (businessId = null) => {
    try {
      const url = businessId 
        ? `${API}/api/admin/super/audit/logs?business_id=${businessId}&limit=50`
        : `${API}/api/admin/super/audit/logs?limit=50`;
      const res = await axios.get(url);
      setAuditLogs(res.data.logs || []);
    } catch (error) {
      console.error("Failed to fetch audit logs:", error);
    }
  };

  useEffect(() => {
    fetchCustomers();
    fetchAuditLogs();
  }, [fetchCustomers]);

  useEffect(() => {
    if (selectedCustomer) {
      fetchOverrideStatus(selectedCustomer.id);
      fetchAuditLogs(selectedCustomer.id);
    }
  }, [selectedCustomer]);

  // Action handlers
  const handleImpersonate = async (readonly = false) => {
    if (!selectedCustomer) return;
    try {
      const res = await axios.post(
        `${API}/api/admin/super/impersonate/start?admin_id=${adminId}&admin_name=${encodeURIComponent(adminName)}&admin_role=${adminRole}`,
        { business_id: selectedCustomer.id, readonly }
      );
      
      // Store impersonation token and redirect
      localStorage.setItem("impersonation_token", res.data.token);
      localStorage.setItem("impersonation_business", JSON.stringify({
        id: res.data.business_id,
        name: res.data.business_name,
        admin_name: res.data.admin_name,
        expires_at: res.data.expires_at
      }));
      
      alert(`Impersonation started for ${res.data.business_name}. Token expires at ${new Date(res.data.expires_at).toLocaleTimeString()}`);
      setImpersonateDialog(false);
    } catch (error) {
      alert(`Failed to start impersonation: ${error.response?.data?.detail || error.message}`);
    }
  };

  const handleExtendTrial = async () => {
    if (!selectedCustomer) return;
    try {
      await axios.post(
        `${API}/api/admin/super/trial/extend?admin_id=${adminId}&admin_name=${encodeURIComponent(adminName)}&admin_role=${adminRole}`,
        { business_id: selectedCustomer.id, days: trialDays, reason: trialReason }
      );
      alert(`Trial extended by ${trialDays} days`);
      setTrialDialog(false);
      fetchOverrideStatus(selectedCustomer.id);
      fetchAuditLogs(selectedCustomer.id);
    } catch (error) {
      alert(`Failed: ${error.response?.data?.detail || error.message}`);
    }
  };

  const handleRestartTrial = async () => {
    if (!selectedCustomer) return;
    try {
      await axios.post(
        `${API}/api/admin/super/trial/restart?admin_id=${adminId}&admin_name=${encodeURIComponent(adminName)}&admin_role=${adminRole}`,
        { business_id: selectedCustomer.id, days: trialDays, reason: trialReason || "Trial restart by admin" }
      );
      alert(`Trial restarted for ${trialDays} days`);
      setTrialDialog(false);
      fetchOverrideStatus(selectedCustomer.id);
      fetchAuditLogs(selectedCustomer.id);
    } catch (error) {
      alert(`Failed: ${error.response?.data?.detail || error.message}`);
    }
  };

  const handleGrantCourtesy = async () => {
    if (!selectedCustomer) return;
    try {
      await axios.post(
        `${API}/api/admin/super/courtesy/grant?admin_id=${adminId}&admin_name=${encodeURIComponent(adminName)}&admin_role=${adminRole}`,
        { 
          business_id: selectedCustomer.id, 
          plan_id: courtesyPlan, 
          expires_in_days: courtesyDays, 
          reason: courtesyReason 
        }
      );
      alert(`Courtesy access granted: ${courtesyPlan} for ${courtesyDays} days`);
      setCourtesyDialog(false);
      fetchOverrideStatus(selectedCustomer.id);
      fetchAuditLogs(selectedCustomer.id);
    } catch (error) {
      alert(`Failed: ${error.response?.data?.detail || error.message}`);
    }
  };

  const handleAssignPlan = async () => {
    if (!selectedCustomer) return;
    try {
      await axios.post(
        `${API}/api/admin/super/plan/assign?admin_id=${adminId}&admin_name=${encodeURIComponent(adminName)}&admin_role=${adminRole}`,
        { business_id: selectedCustomer.id, plan_id: assignPlan, reason: assignReason }
      );
      alert(`Plan ${assignPlan} assigned`);
      setPlanDialog(false);
      fetchOverrideStatus(selectedCustomer.id);
      fetchAuditLogs(selectedCustomer.id);
    } catch (error) {
      alert(`Failed: ${error.response?.data?.detail || error.message}`);
    }
  };

  const handleSuspend = async () => {
    if (!selectedCustomer) return;
    try {
      await axios.post(
        `${API}/api/admin/super/subscription/suspend?admin_id=${adminId}&admin_name=${encodeURIComponent(adminName)}&admin_role=${adminRole}`,
        { business_id: selectedCustomer.id, reason: suspendReason }
      );
      alert("Subscription suspended");
      setSuspendDialog(false);
      fetchOverrideStatus(selectedCustomer.id);
      fetchAuditLogs(selectedCustomer.id);
    } catch (error) {
      alert(`Failed: ${error.response?.data?.detail || error.message}`);
    }
  };

  const handleUnsuspend = async () => {
    if (!selectedCustomer) return;
    try {
      await axios.post(
        `${API}/api/admin/super/subscription/unsuspend?admin_id=${adminId}&admin_name=${encodeURIComponent(adminName)}&admin_role=${adminRole}`,
        { business_id: selectedCustomer.id, reason: "Admin unsuspension" }
      );
      alert("Subscription unsuspended");
      fetchOverrideStatus(selectedCustomer.id);
      fetchAuditLogs(selectedCustomer.id);
    } catch (error) {
      alert(`Failed: ${error.response?.data?.detail || error.message}`);
    }
  };

  const handleSendNotification = async () => {
    if (!selectedCustomer) return;
    try {
      await axios.post(`${API}/api/dunning/notify`, {
        business_id: selectedCustomer.id,
        notification_type: notificationType
      });
      alert(`${notificationType} notification sent`);
      setNotifyDialog(false);
    } catch (error) {
      alert(`Failed: ${error.response?.data?.detail || error.message}`);
    }
  };

  const filteredCustomers = customers.filter(c => 
    c.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusBadge = (status) => {
    const styles = {
      active: "bg-green-500/20 text-green-400",
      suspended: "bg-red-500/20 text-red-400",
      past_due: "bg-yellow-500/20 text-yellow-400",
      cancelled: "bg-gray-500/20 text-gray-400"
    };
    return <Badge className={styles[status] || styles.active}>{status}</Badge>;
  };

  const getActionBadge = (action) => {
    const colors = {
      impersonate_start: "bg-purple-500/20 text-purple-400",
      impersonate_end: "bg-purple-500/20 text-purple-400",
      trial_extend: "bg-blue-500/20 text-blue-400",
      trial_restart: "bg-blue-500/20 text-blue-400",
      courtesy_access_grant: "bg-green-500/20 text-green-400",
      courtesy_access_revoke: "bg-orange-500/20 text-orange-400",
      plan_assign: "bg-cyan-500/20 text-cyan-400",
      subscription_suspend: "bg-red-500/20 text-red-400",
      subscription_unsuspend: "bg-green-500/20 text-green-400"
    };
    return <Badge className={colors[action] || "bg-gray-500/20 text-gray-400"}>{action.replace(/_/g, " ")}</Badge>;
  };

  return (
    <div className="p-6 space-y-6 bg-slate-900 min-h-screen">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Billing & Admin Controls</h1>
          <p className="text-slate-400">Impersonation, billing overrides, and subscription management</p>
        </div>
        <Badge className="bg-purple-500/20 text-purple-400">
          <Shield className="w-4 h-4 mr-1" /> Super Admin
        </Badge>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-800">
          <TabsTrigger value="customers" className="data-[state=active]:bg-slate-700">
            <Building className="w-4 h-4 mr-2" /> Customers
          </TabsTrigger>
          <TabsTrigger value="audit" className="data-[state=active]:bg-slate-700">
            <History className="w-4 h-4 mr-2" /> Audit Logs
          </TabsTrigger>
        </TabsList>

        <TabsContent value="customers" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Customer List */}
            <Card className="bg-slate-800 border-slate-700 lg:col-span-1">
              <CardHeader>
                <CardTitle className="text-white text-lg">Customers</CardTitle>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input 
                    placeholder="Search customers..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9 bg-slate-900 border-slate-600 text-white"
                  />
                </div>
              </CardHeader>
              <CardContent className="max-h-[600px] overflow-y-auto space-y-2">
                {loading ? (
                  <p className="text-slate-400 text-center py-4">Loading...</p>
                ) : filteredCustomers.length === 0 ? (
                  <p className="text-slate-400 text-center py-4">No customers found</p>
                ) : (
                  filteredCustomers.map(customer => (
                    <div
                      key={customer.id}
                      onClick={() => setSelectedCustomer(customer)}
                      className={`p-3 rounded-lg cursor-pointer transition-colors ${
                        selectedCustomer?.id === customer.id 
                          ? "bg-blue-600/20 border border-blue-500" 
                          : "bg-slate-900 hover:bg-slate-700"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <p className="text-white font-medium truncate">{customer.name}</p>
                        {getStatusBadge(customer.subscription_status)}
                      </div>
                      <p className="text-slate-400 text-sm truncate">{customer.email}</p>
                      <p className="text-slate-500 text-xs mt-1">
                        Plan: {customer.subscription_plan?.replace("_", " ")}
                      </p>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>

            {/* Customer Details & Actions */}
            <Card className="bg-slate-800 border-slate-700 lg:col-span-2">
              {selectedCustomer ? (
                <>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-white">{selectedCustomer.name}</CardTitle>
                        <CardDescription>{selectedCustomer.email}</CardDescription>
                      </div>
                      <div className="flex gap-2">
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => setImpersonateDialog(true)}
                          className="border-purple-500 text-purple-400 hover:bg-purple-500/20"
                        >
                          <Eye className="w-4 h-4 mr-1" /> View as Customer
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {/* Override Status */}
                    {overrideStatus && (
                      <div className="space-y-4">
                        <h3 className="text-white font-medium">Current Status</h3>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          <div className="bg-slate-900 p-4 rounded-lg">
                            <p className="text-slate-400 text-sm">Plan</p>
                            <p className="text-white font-medium">{overrideStatus.current_plan?.replace("_", " ")}</p>
                          </div>
                          <div className="bg-slate-900 p-4 rounded-lg">
                            <p className="text-slate-400 text-sm">Status</p>
                            {getStatusBadge(overrideStatus.subscription_status)}
                          </div>
                          <div className="bg-slate-900 p-4 rounded-lg">
                            <p className="text-slate-400 text-sm">Override Active</p>
                            <p className="text-white font-medium">
                              {overrideStatus.has_override ? (
                                <Badge className="bg-yellow-500/20 text-yellow-400">Yes</Badge>
                              ) : "No"}
                            </p>
                          </div>
                          <div className="bg-slate-900 p-4 rounded-lg">
                            <p className="text-slate-400 text-sm">Trial Extended</p>
                            <p className="text-white font-medium">
                              {overrideStatus.trial_extended ? "Yes" : "No"}
                            </p>
                          </div>
                        </div>

                        {overrideStatus.has_override && (
                          <Alert className="bg-yellow-500/10 border-yellow-500/50">
                            <AlertTriangle className="w-4 h-4 text-yellow-400" />
                            <AlertDescription className="text-yellow-200">
                              <strong>Override Active:</strong> {overrideStatus.override_type?.replace("_", " ")}
                              {overrideStatus.override_expires_at && (
                                <span> — Expires: {new Date(overrideStatus.override_expires_at).toLocaleDateString()}</span>
                              )}
                              {overrideStatus.override_reason && (
                                <span className="block mt-1">Reason: {overrideStatus.override_reason}</span>
                              )}
                            </AlertDescription>
                          </Alert>
                        )}

                        {overrideStatus.is_suspended && (
                          <Alert className="bg-red-500/10 border-red-500/50">
                            <XCircle className="w-4 h-4 text-red-400" />
                            <AlertDescription className="text-red-200">
                              <strong>Account Suspended</strong>
                              {overrideStatus.suspension_reason && (
                                <span className="block mt-1">Reason: {overrideStatus.suspension_reason}</span>
                              )}
                            </AlertDescription>
                          </Alert>
                        )}
                      </div>
                    )}

                    {/* Action Buttons */}
                    <div className="space-y-4">
                      <h3 className="text-white font-medium">Actions</h3>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => setTrialDialog(true)}
                          className="border-blue-500 text-blue-400 hover:bg-blue-500/20"
                        >
                          <Clock className="w-4 h-4 mr-1" /> Trial Control
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => setCourtesyDialog(true)}
                          className="border-green-500 text-green-400 hover:bg-green-500/20"
                        >
                          <Gift className="w-4 h-4 mr-1" /> Courtesy Access
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => setPlanDialog(true)}
                          className="border-cyan-500 text-cyan-400 hover:bg-cyan-500/20"
                        >
                          <CreditCard className="w-4 h-4 mr-1" /> Assign Plan
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => setNotifyDialog(true)}
                          className="border-yellow-500 text-yellow-400 hover:bg-yellow-500/20"
                        >
                          <MessageSquare className="w-4 h-4 mr-1" /> Send Notice
                        </Button>
                        {overrideStatus?.is_suspended ? (
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={handleUnsuspend}
                            className="border-green-500 text-green-400 hover:bg-green-500/20"
                          >
                            <Play className="w-4 h-4 mr-1" /> Unsuspend
                          </Button>
                        ) : (
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => setSuspendDialog(true)}
                            className="border-red-500 text-red-400 hover:bg-red-500/20"
                          >
                            <Pause className="w-4 h-4 mr-1" /> Suspend
                          </Button>
                        )}
                      </div>
                    </div>

                    {/* Recent Audit Logs for Customer */}
                    <div className="space-y-4">
                      <h3 className="text-white font-medium">Recent Activity</h3>
                      <div className="space-y-2 max-h-[300px] overflow-y-auto">
                        {auditLogs.slice(0, 10).map(log => (
                          <div key={log.id} className="bg-slate-900 p-3 rounded-lg">
                            <div className="flex items-center justify-between">
                              {getActionBadge(log.action)}
                              <span className="text-slate-500 text-xs">
                                {new Date(log.timestamp).toLocaleString()}
                              </span>
                            </div>
                            <p className="text-slate-300 text-sm mt-1">
                              By: {log.admin_name}
                            </p>
                            {log.reason && (
                              <p className="text-slate-400 text-xs mt-1">
                                Reason: {log.reason}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </>
              ) : (
                <CardContent className="flex items-center justify-center h-[500px]">
                  <div className="text-center">
                    <User className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                    <p className="text-slate-400">Select a customer to view details and perform actions</p>
                  </div>
                </CardContent>
              )}
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="audit">
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white">All Audit Logs</CardTitle>
              <CardDescription>Complete history of admin actions</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-[600px] overflow-y-auto">
                {auditLogs.map(log => (
                  <div key={log.id} className="bg-slate-900 p-4 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-3">
                        {getActionBadge(log.action)}
                        <span className="text-white font-medium">{log.business_name || log.business_id}</span>
                      </div>
                      <span className="text-slate-500 text-sm">
                        {new Date(log.timestamp).toLocaleString()}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 text-sm">
                      <span className="text-slate-400">Admin: {log.admin_name}</span>
                      {log.reason && <span className="text-slate-500">Reason: {log.reason}</span>}
                    </div>
                    {log.details && Object.keys(log.details).length > 0 && (
                      <div className="mt-2 text-xs text-slate-500">
                        {JSON.stringify(log.details)}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Impersonate Dialog */}
      <Dialog open={impersonateDialog} onOpenChange={setImpersonateDialog}>
        <DialogContent className="bg-slate-800 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white">View as Customer</DialogTitle>
            <DialogDescription>
              Start an impersonation session to view {selectedCustomer?.name}'s dashboard.
              Token expires in 15 minutes.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <Alert className="bg-purple-500/10 border-purple-500/50">
              <Eye className="w-4 h-4 text-purple-400" />
              <AlertDescription className="text-purple-200">
                All actions during impersonation are logged for audit purposes.
              </AlertDescription>
            </Alert>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setImpersonateDialog(false)}>Cancel</Button>
            <Button onClick={() => handleImpersonate(true)} variant="outline" className="border-blue-500 text-blue-400">
              <EyeOff className="w-4 h-4 mr-1" /> View Only
            </Button>
            <Button onClick={() => handleImpersonate(false)} className="bg-purple-600 hover:bg-purple-700">
              <Eye className="w-4 h-4 mr-1" /> Full Access
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Trial Control Dialog */}
      <Dialog open={trialDialog} onOpenChange={setTrialDialog}>
        <DialogContent className="bg-slate-800 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white">Trial Control</DialogTitle>
            <DialogDescription>Extend or restart the trial period for {selectedCustomer?.name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Days</Label>
              <Input 
                type="number" 
                value={trialDays} 
                onChange={(e) => setTrialDays(parseInt(e.target.value))}
                className="bg-slate-900 border-slate-600 text-white"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Reason</Label>
              <Textarea 
                value={trialReason} 
                onChange={(e) => setTrialReason(e.target.value)}
                placeholder="Reason for trial extension/restart"
                className="bg-slate-900 border-slate-600 text-white"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTrialDialog(false)}>Cancel</Button>
            <Button onClick={handleExtendTrial} className="bg-blue-600 hover:bg-blue-700">
              <Clock className="w-4 h-4 mr-1" /> Extend Trial
            </Button>
            <Button onClick={handleRestartTrial} className="bg-green-600 hover:bg-green-700">
              <RefreshCw className="w-4 h-4 mr-1" /> Restart Trial
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Courtesy Access Dialog */}
      <Dialog open={courtesyDialog} onOpenChange={setCourtesyDialog}>
        <DialogContent className="bg-slate-800 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white">Grant Courtesy Access</DialogTitle>
            <DialogDescription>Provide paid access without Stripe subscription</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Plan</Label>
              <Select value={courtesyPlan} onValueChange={setCourtesyPlan}>
                <SelectTrigger className="bg-slate-900 border-slate-600 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="starter">Starter</SelectItem>
                  <SelectItem value="professional">Professional</SelectItem>
                  <SelectItem value="enterprise">Enterprise</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Duration (days)</Label>
              <Input 
                type="number" 
                value={courtesyDays} 
                onChange={(e) => setCourtesyDays(parseInt(e.target.value))}
                className="bg-slate-900 border-slate-600 text-white"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Reason</Label>
              <Textarea 
                value={courtesyReason} 
                onChange={(e) => setCourtesyReason(e.target.value)}
                placeholder="Reason for courtesy access"
                className="bg-slate-900 border-slate-600 text-white"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCourtesyDialog(false)}>Cancel</Button>
            <Button onClick={handleGrantCourtesy} className="bg-green-600 hover:bg-green-700">
              <Gift className="w-4 h-4 mr-1" /> Grant Access
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Plan Dialog */}
      <Dialog open={planDialog} onOpenChange={setPlanDialog}>
        <DialogContent className="bg-slate-800 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white">Assign Plan</DialogTitle>
            <DialogDescription>Manually assign a plan without checkout</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Plan</Label>
              <Select value={assignPlan} onValueChange={setAssignPlan}>
                <SelectTrigger className="bg-slate-900 border-slate-600 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="free_trial">Free Trial</SelectItem>
                  <SelectItem value="starter">Starter</SelectItem>
                  <SelectItem value="professional">Professional</SelectItem>
                  <SelectItem value="enterprise">Enterprise</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Reason</Label>
              <Textarea 
                value={assignReason} 
                onChange={(e) => setAssignReason(e.target.value)}
                placeholder="Reason for manual plan assignment"
                className="bg-slate-900 border-slate-600 text-white"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPlanDialog(false)}>Cancel</Button>
            <Button onClick={handleAssignPlan} className="bg-cyan-600 hover:bg-cyan-700">
              <CreditCard className="w-4 h-4 mr-1" /> Assign Plan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Suspend Dialog */}
      <Dialog open={suspendDialog} onOpenChange={setSuspendDialog}>
        <DialogContent className="bg-slate-800 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white">Suspend Account</DialogTitle>
            <DialogDescription>This will suspend all services for {selectedCustomer?.name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <Alert className="bg-red-500/10 border-red-500/50">
              <AlertTriangle className="w-4 h-4 text-red-400" />
              <AlertDescription className="text-red-200">
                This action will immediately disable all services for this customer.
              </AlertDescription>
            </Alert>
            <div className="space-y-2">
              <Label className="text-slate-300">Reason (required)</Label>
              <Textarea 
                value={suspendReason} 
                onChange={(e) => setSuspendReason(e.target.value)}
                placeholder="Reason for suspension"
                className="bg-slate-900 border-slate-600 text-white"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSuspendDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleSuspend} 
              disabled={!suspendReason}
              className="bg-red-600 hover:bg-red-700"
            >
              <Pause className="w-4 h-4 mr-1" /> Suspend Account
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Notification Dialog */}
      <Dialog open={notifyDialog} onOpenChange={setNotifyDialog}>
        <DialogContent className="bg-slate-800 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white">Send Billing Notification</DialogTitle>
            <DialogDescription>Send a WhatsApp notification to {selectedCustomer?.name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Notification Type</Label>
              <Select value={notificationType} onValueChange={setNotificationType}>
                <SelectTrigger className="bg-slate-900 border-slate-600 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="payment_reminder">Payment Reminder</SelectItem>
                  <SelectItem value="payment_due_tomorrow">Payment Due Tomorrow</SelectItem>
                  <SelectItem value="payment_failed">Payment Failed</SelectItem>
                  <SelectItem value="grace_period_warning">Grace Period Warning</SelectItem>
                  <SelectItem value="invoice_generated">Invoice Generated</SelectItem>
                  <SelectItem value="payment_received">Payment Received</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNotifyDialog(false)}>Cancel</Button>
            <Button onClick={handleSendNotification} className="bg-yellow-600 hover:bg-yellow-700">
              <Send className="w-4 h-4 mr-1" /> Send Notification
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
